import type { AuthResponse, AuthSession } from '../types/auth.types';
import { clearRefreshToken, loadRefreshToken, saveRefreshToken } from './session-storage';

type Listener = () => void;

function buildSession(response: AuthResponse): AuthSession {
  return {
    accessToken: response.accessToken,
    refreshToken: response.refreshToken,
    email: response.email,
    expiresAt: Date.now() + response.expiresIn * 1000,
  };
}

/**
 * Session (singleton) : access token en mémoire uniquement, refresh token
 * persisté en sessionStorage (session-storage.ts) pour survivre au rechargement.
 */
class SessionManager {
  private session: AuthSession | null = null;
  private listeners = new Set<Listener>();

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): AuthSession | null => this.session;

  getSession = (): AuthSession | null => this.session;

  getAccessToken = (): string | null => this.session?.accessToken ?? null;

  getRefreshToken = (): string | null => this.session?.refreshToken ?? null;

  setSession(response: AuthResponse): void {
    this.session = buildSession(response);
    saveRefreshToken(response.refreshToken);
    this.emit();
  }

  clearSession(): void {
    const hadSession = this.session !== null;
    this.session = null;
    clearRefreshToken();
    if (hadSession) this.emit();
  }

  /** Refresh token persisté en sessionStorage (pour la restauration au boot). */
  getPersistedRefreshToken(): string | null {
    return loadRefreshToken();
  }

  hasPersistedRefreshToken(): boolean {
    return loadRefreshToken() !== null;
  }

  isAuthenticated(): boolean {
    return this.session !== null && this.session.expiresAt > Date.now();
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }
}

export const sessionManager = new SessionManager();
